'use client';

import { useState } from 'react';
import { useParams } from 'next/navigation';
import { FormProvider, useForm } from 'react-hook-form';
import ControlledInput from 'src/components/ControlledFields/ControlledInput';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { useDashboard } from 'src/context/providers/DashboardProvider';
import {
  useGetCompanyByIdQuery,
  useGetAllCompaniesQuery,
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
} from 'src/api/redux';
import { Dialog } from 'src/components/PulseUI/Dialog';
import { PulseButton } from 'src/components/PulseUI/PulseButton';
import { HubSpotCompanyField } from 'src/components/Settings/Companies/components/HubSpotCompanyField';
import { COMPANY_NAME_MAX_LENGTH_RULE } from 'src/components/Settings/Companies/components/HubSpotCompanyField/constants';
import { getCompanySaveError } from 'src/components/Settings/Companies/components/HubSpotCompanyField/utils';

interface AddEditCompanyDialogProps {
  open: boolean;
  onClose: () => void;
}

const AddEditCompanyDialog = ({ onClose }: AddEditCompanyDialogProps) => {
  const { companyId } = useParams<{ companyId: string }>();
  const log = useLogging();
  const { setNotification } = useNotification();
  const { showAddEditCompanyModal } = useDashboard();

  // currentData, not data: the dialog lives in the settings layout, and a
  // skipped query keeps returning the last company viewed, so Add Company on
  // the companies table would open as an edit of that company.
  const { currentData: currentCompany } = useGetCompanyByIdQuery(
    { companyId },
    { skip: !companyId }
  );
  const { refetch: refetchAllCompanies } = useGetAllCompaniesQuery({});
  const [createCompanyMutation] = useCreateCompanyMutation();
  const [updateCompanyMutation] = useUpdateCompanyMutation();

  const [isLoading, setIsLoading] = useState(false);

  const {
    name = '',
    max_seats = 0,
    hubspot_company_id = null,
    hubspot_company_name = null,
    hubspot_domain = null,
  } = currentCompany || {};

  const defaultValues = {
    name,
    // Blank rather than 0 when creating: 0 satisfies `required` (and renders
    // as an empty box), so the seat count could be skipped without noticing.
    max_seats: currentCompany ? max_seats : '',
    hubspot_company_id,
    hubspot_company_name,
    hubspot_domain,
  };

  const methods = useForm({
    values: {
      ...defaultValues,
    },
  });

  const {
    handleSubmit,
    reset,
    setError,
    formState: { isDirty, isValid },
  } = methods;

  // The dialog stays mounted between opens, so every close clears the form
  // for the next one (editing resets to the company's saved values).
  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: Record<string, unknown>) => {
    const isEdit = !!currentCompany;
    setIsLoading(true);
    try {
      if (isEdit) {
        await updateCompanyMutation({ companyId, ...data }).unwrap();
        log.event('Company updated', {
          source: 'AddEditCompany',
          companyId,
          data,
        });
        setNotification({
          title: 'Success',
          message: 'Company updated',
          type: 'success',
        });
      } else {
        await createCompanyMutation(data).unwrap();

        log.event('Company created', {
          source: 'AddEditCompany',
          data,
        });
        setNotification({
          title: 'Success',
          message: 'Company created',
          type: 'success',
        });
      }
      handleClose();
    } catch (err: unknown) {
      const { message, isHubSpotLinkError } = getCompanySaveError(
        err,
        'Error creating company'
      );
      // The dialog stays open either way so no input is lost.
      if (isHubSpotLinkError) {
        setError('hubspot_company_id', { message });
      } else {
        log.exception('Error creating company', err);
        setNotification({
          title: 'Error',
          message,
          type: 'error',
        });
      }
    } finally {
      refetchAllCompanies();
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      isOpen={showAddEditCompanyModal}
      onClose={handleClose}
      size="lg"
      className="!h-auto"
      showCloseButton
      data-testid="add-edit-company-dialog"
    >
      <Dialog.Header>
        <h2
          className="text-lg font-semibold"
          data-testid="add-edit-company-dialog-title"
        >
          {currentCompany ? 'Edit' : 'Add'} Company
        </h2>
      </Dialog.Header>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} data-testid="company-form">
          <Dialog.Content
            className="p-6 flex flex-col gap-4"
            data-testid="company-dialog-content"
          >
            {/* First, so the name can come from HubSpot rather than be typed.
                A new company must be linked and a linked customer can't be
                unlinked (MKT-869). Editing doesn't require a link, so an
                unlinked legacy customer can still be saved. */}
            <HubSpotCompanyField
              isOpen={showAddEditCompanyModal}
              isEdit={!!currentCompany}
              linkRequired={!currentCompany}
              canUnlinkSaved={!currentCompany || !!currentCompany?.is_demo}
            />
            <div className="flex gap-4 w-full">
              <div className="flex-[2_0_0%]">
                <label
                  className="text-sm font-semibold text-neutral-700 mb-2 block"
                  data-testid="company-name-label"
                >
                  Name
                </label>
                <ControlledInput
                  name="name"
                  placeholder="Name"
                  rules={{
                    required: 'This field is required',
                    maxLength: COMPANY_NAME_MAX_LENGTH_RULE,
                  }}
                  fullWidth
                  useLabel={false}
                  data-testid="company-name-input"
                />
              </div>
              <div className="flex-[1_0_0%]">
                <label
                  className="text-sm font-semibold text-neutral-700 mb-2 block"
                  data-testid="max-seats-label"
                >
                  Max Users
                </label>
                <ControlledInput
                  type="number"
                  min={0}
                  name="max_seats"
                  placeholder="Max Seats"
                  rules={{
                    required: 'This field is required',
                    // Existing companies can legitimately hold 0 (never set),
                    // so only a new company must have a real seat count.
                    ...(!currentCompany && {
                      min: { value: 1, message: 'Enter at least 1 seat' },
                    }),
                  }}
                  useLabel={false}
                  data-testid="max-seats-input"
                />
              </div>
            </div>
          </Dialog.Content>
          <Dialog.Actions data-testid="company-dialog-actions">
            <PulseButton
              variant="outline"
              color="neutral"
              onClick={handleClose}
              aria-label="Cancel"
              data-testid="company-cancel-button"
            >
              Cancel
            </PulseButton>
            <PulseButton
              type="submit"
              variant="solid"
              color="primary"
              loading={isLoading}
              onClick={handleSubmit(onSubmit)}
              disabled={!isDirty || !isValid}
              aria-label={currentCompany ? 'Update Company' : 'Create Company'}
              data-testid="company-submit-button"
            >
              {currentCompany ? 'Update Company' : 'Create Company'}
            </PulseButton>
          </Dialog.Actions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default AddEditCompanyDialog;
